import { useState } from "react";
import { MessageSquare, Sparkles, Loader2, Star, Settings, Users, ChevronDown, ChevronUp, GraduationCap, BookOpen, Zap, Bell, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useUsers } from "@/hooks/useUsers";
import { useSkills } from "@/hooks/useSkills";
import { useProfile } from "@/hooks/useProfile";
import { UserProfile } from "@/hooks/useUsers";
import ManageSkillsDialog from "@/components/ManageSkillsDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePendingMatches } from "@/hooks/usePendingMatches";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";
import { OnboardingTour, useOnboardingTour } from "@/components/OnboardingTour";
import SwipeStack from "@/components/SwipeStack";
import { useSwipeHistory } from "@/hooks/useSwipeHistory";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface SkillMatch {
  user: UserProfile;
  teachingMatches: { emoji: string; name: string }[];
  learningMatches: { emoji: string; name: string }[];
}

const Match = () => {
  const [showManageSkills, setShowManageSkills] = useState(false);
  const [skillsExpanded, setSkillsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState("match");
  
  const { user, isAuthenticated, requireAuth, showAuthPrompt, closeAuthPrompt, promptOptions } = useRequireAuth();
  const navigate = useNavigate();
  const { users, loading: usersLoading } = useUsers(false);
  const { teachingSkills, learningSkills, loading: skillsLoading } = useSkills();
  const { toast } = useToast();
  const { profile } = useProfile();
  const { showTour, completeTour } = useOnboardingTour();
  const { pendingCount } = usePendingMatches();
  const { unreadCount } = useUnreadMessages();
  const { saveSwipe, undoSwipe, getSwipedUserIds, getLikedUserIdsSorted, isRecentMatch, loading: swipeLoading } = useSwipeHistory();

  const totalNotifications = pendingCount + unreadCount;

  // Filter out current user and already swiped users
  const swipedUserIds = getSwipedUserIds();
  const likedUserIdsSorted = getLikedUserIdsSorted();
  const allUsers = users.filter(u => u.id !== user?.id);
  const unswipedUsers = allUsers.filter(u => !swipedUserIds.includes(u.id));
  
  // Get users that the current user has liked (swiped right on), sorted by most recent
  const likedUsers = likedUserIdsSorted
    .map(id => allUsers.find(u => u.id === id))
    .filter((u): u is UserProfile => u !== undefined);

  // Get users with skill matches
  const getMatchedUsers = () => {
    const myTeachingSkillNames = teachingSkills.map(s => s.skill.name.toLowerCase());
    const myLearningSkillNames = learningSkills.map(s => s.skill.name.toLowerCase());
    
    return unswipedUsers.filter(targetUser => {
      const teachingMatches = targetUser.teaching_skills.some(
        skill => myLearningSkillNames.includes(skill.name.toLowerCase())
      );
      const learningMatches = targetUser.learning_skills.some(
        skill => myTeachingSkillNames.includes(skill.name.toLowerCase())
      );
      return teachingMatches || learningMatches;
    });
  };

  // Calculate match percentage for a user
  const calculateMatchPercentage = (targetUser: UserProfile): number => {
    const myTeachingSkillNames = teachingSkills.map(s => s.skill.name.toLowerCase());
    const myLearningSkillNames = learningSkills.map(s => s.skill.name.toLowerCase());
    
    const theyCanTeachMeCount = targetUser.teaching_skills.filter(
      skill => myLearningSkillNames.includes(skill.name.toLowerCase())
    ).length;
    
    const iCanTeachThemCount = targetUser.learning_skills.filter(
      skill => myTeachingSkillNames.includes(skill.name.toLowerCase())
    ).length;
    
    const totalPossible = Math.max(1, myLearningSkillNames.length + myTeachingSkillNames.length);
    const totalMatches = theyCanTeachMeCount + iCanTeachThemCount;
    
    return Math.min(100, Math.round((totalMatches / totalPossible) * 100 * 2));
  };

  // Get all users sorted by updated_at (latest first)
  const everyoneUsers = [...allUsers].sort((a, b) => {
    const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
    const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
    return dateB - dateA;
  });

  const matchedUsers = getMatchedUsers();

  // Check if user was active in last 24 hours
  const isRecentlyActive = (user: UserProfile): boolean => {
    if (!user.updated_at) return false;
    const updatedAt = new Date(user.updated_at).getTime();
    const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
    return updatedAt > twentyFourHoursAgo;
  };

  // Check for skill matches
  const checkSkillMatch = (targetUser: UserProfile): SkillMatch | null => {
    const myTeachingSkillNames = teachingSkills.map(s => s.skill.name.toLowerCase());
    const myLearningSkillNames = learningSkills.map(s => s.skill.name.toLowerCase());
    
    const teachingMatches = targetUser.teaching_skills.filter(
      skill => myLearningSkillNames.includes(skill.name.toLowerCase())
    ).map(s => ({ emoji: s.emoji, name: s.name }));
    
    const learningMatches = targetUser.learning_skills.filter(
      skill => myTeachingSkillNames.includes(skill.name.toLowerCase())
    ).map(s => ({ emoji: s.emoji, name: s.name }));
    
    if (teachingMatches.length > 0 || learningMatches.length > 0) {
      return { user: targetUser, teachingMatches, learningMatches };
    }
    
    return null;
  };

  const handleSwipeLeft = async (swipedUser: UserProfile) => {
    requireAuth(async () => {
      await saveSwipe(swipedUser.id, "left");
      toast({
        description: `Passed on ${swipedUser.name}`,
        duration: 1500,
      });
    }, { title: "Join to Swipe", description: "Sign up to start discovering and connecting with skill sharers!" });
  };

  const handleSwipeRight = async (swipedUser: UserProfile, isSuperLike: boolean = false) => {
    requireAuth(async () => {
      await saveSwipe(swipedUser.id, "right", isSuperLike);
      const match = checkSkillMatch(swipedUser);
      
      if (isSuperLike) {
        toast({
          title: "Super Like sent! ⭐",
          description: `You super liked ${swipedUser.name}`,
          duration: 2000,
        });
      } else if (match) {
        toast({
          title: "It's a match! 🎉",
          description: `You and ${swipedUser.name} have matching skills`,
          duration: 2000,
        });
      } else {
        toast({
          title: "Connection sent! 💫",
          description: `Liked ${swipedUser.name}`,
          duration: 2000,
        });
      }
    }, { title: "Join to Connect", description: "Sign up to like profiles and start matching with learners!" });
  };

  const handleUndo = async (targetUser: UserProfile) => {
    requireAuth(async () => {
      await undoSwipe(targetUser.id);
      toast({
        description: `Restored ${targetUser.name}`,
        duration: 1500,
      });
    }, { title: "Join to Undo", description: "Sign up to undo your swipes!" });
  };

  const handleUserClick = (targetUser: UserProfile) => {
    navigate(`/profile?id=${targetUser.id}`);
  };

  const handleStartChat = (userId: string) => {
    requireAuth(() => {
      navigate(`/chat?userId=${userId}`);
    }, { title: "Join to Chat", description: "Sign up to start chatting with other learners!" });
  };

  if (usersLoading || skillsLoading || swipeLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Auth Prompt Dialog */}
      <AuthPromptDialog 
        open={showAuthPrompt} 
        onOpenChange={closeAuthPrompt}
        title={promptOptions.title}
        description={promptOptions.description}
      />
      
      {/* Onboarding Tour */}
      <OnboardingTour isVisible={showTour && isAuthenticated} onComplete={completeTour} />
      
      {/* Header */}
      <header className="gradient-primary text-white p-4 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1">
              <h1 className="text-xl font-bold flex items-center gap-2 mb-1">
                <Zap className="h-5 w-5" />
                Swipe
              </h1>
              <p className="text-white/90 text-xs">
                Find your next learning partner
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Guest Mode Indicator */}
              {!isAuthenticated && (
                <Badge 
                  variant="secondary" 
                  className="bg-white/20 text-white border-white/30 text-xs cursor-pointer hover:bg-white/30"
                  onClick={() => navigate('/auth')}
                >
                  Guest Mode
                </Badge>
              )}
              
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20 relative"
                onClick={() => navigate('/notifications')}
              >
                <Bell className="h-5 w-5" />
                {totalNotifications > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-xs flex items-center justify-center"
                  >
                    {totalNotifications > 99 ? '99+' : totalNotifications}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Your Skills Card - Only show for authenticated users */}
        {isAuthenticated ? (
          <Card className="shadow-soft overflow-hidden">
            <Collapsible open={skillsExpanded} onOpenChange={setSkillsExpanded}>
              <CollapsibleTrigger asChild>
                <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-accent/50 transition-colors">
                  <h3 className="font-bold text-base">Your Skills</h3>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowManageSkills(true);
                      }}
                      className="gap-2 h-8"
                    >
                      <Settings className="h-4 w-4" />
                      Edit
                    </Button>
                    {skillsExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="px-4 pb-4 space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">I can teach:</p>
                    {teachingSkills.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {teachingSkills.slice(0, 4).map((item) => (
                          <Badge key={item.id} className="bg-primary text-xs">
                            {item.skill.emoji} {item.skill.name}
                          </Badge>
                        ))}
                        {teachingSkills.length > 4 && (
                          <Badge variant="secondary" className="text-xs">+{teachingSkills.length - 4}</Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No teaching skills yet</p>
                    )}
                  </div>
                  
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">I want to learn:</p>
                    {learningSkills.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {learningSkills.slice(0, 4).map((item) => (
                          <Badge key={item.id} variant="outline" className="text-xs">
                            {item.skill.emoji} {item.skill.name}
                          </Badge>
                        ))}
                        {learningSkills.length > 4 && (
                          <Badge variant="secondary" className="text-xs">+{learningSkills.length - 4}</Badge>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No learning goals yet</p>
                    )}
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </Card>
        ) : (
          <Card className="shadow-soft p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <span className="font-medium text-sm">Login or Signup and add your skills to find matches</span>
              </div>
              <Button 
                size="sm" 
                onClick={() => navigate('/auth')}
                className="gap-2"
              >
                Get Started
              </Button>
            </div>
          </Card>
        )}

        {/* Users Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="match" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              Match ({matchedUsers.length > 0 ? matchedUsers.length : likedUsers.length})
            </TabsTrigger>
            <TabsTrigger value="everyone" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Everyone ({everyoneUsers.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Match Tab - Swipe Cards or Matched Profiles */}
          <TabsContent value="match" className="mt-4">
            {/* Show SwipeStack if there are unswiped matches */}
            {matchedUsers.length > 0 && (
              <SwipeStack
                users={matchedUsers}
                onSwipeLeft={handleSwipeLeft}
                onSwipeRight={handleSwipeRight}
                onUndo={handleUndo}
                calculateMatchPercentage={calculateMatchPercentage}
                onEmpty={() => {
                  toast({
                    title: "All caught up! 🎉",
                    description: "You've seen all available matches. Check back later!",
                  });
                }}
              />
            )}
            
            {/* Show liked profiles when no more matches to swipe - only for authenticated users */}
            {isAuthenticated && matchedUsers.length === 0 && likedUsers.length > 0 && (
              <div className="space-y-3">
                <div className="text-center mb-4">
                  <h4 className="font-semibold text-lg">Your Matches</h4>
                  <p className="text-sm text-muted-foreground">
                    People you've connected with
                  </p>
                </div>
                {likedUsers.map((targetUser) => {
                  const match = checkSkillMatch(targetUser);
                  const hasMatch = match !== null;
                  const recentlyActive = isRecentlyActive(targetUser);
                  const isNew = isRecentMatch(targetUser.id);
                  return (
                    <Card 
                      key={targetUser.id} 
                      className={`p-4 shadow-soft cursor-pointer transition-all hover:shadow-md ${hasMatch ? 'border-primary/50 bg-primary/5' : ''}`}
                      onClick={() => handleUserClick(targetUser)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <Avatar className="h-14 w-14 border-2 border-primary/20">
                            <AvatarImage 
                              src={targetUser.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${targetUser.id}`} 
                              alt={targetUser.name || "User"} 
                            />
                            <AvatarFallback className="bg-primary text-primary-foreground">
                              {(targetUser.name || "U").charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          {isNew && (
                            <Badge className="absolute -top-1 -right-1 bg-green-500 text-white text-[10px] px-1.5 py-0 h-4">
                              New
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold truncate text-foreground">{targetUser.name || "New User"}</h4>
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="h-4 w-4 fill-current" />
                              <span className="text-sm font-medium">{(targetUser.rating || 0).toFixed(1)}</span>
                            </div>
                          </div>
                          
                          {targetUser.bio && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{targetUser.bio}</p>
                          )}
                          
                          {hasMatch && (
                            <div className="space-y-1.5 mb-2">
                              {match.teachingMatches.length > 0 && (
                                <div>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                                    <GraduationCap className="h-3 w-3 text-green-500" />
                                    Can teach you:
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {match.teachingMatches.slice(0, 3).map(skill => (
                                      <Badge key={skill.name} className="bg-green-500/10 text-green-700 border-green-500/30 text-xs">
                                        {skill.emoji} {skill.name}
                                      </Badge>
                                    ))}
                                    {match.teachingMatches.length > 3 && (
                                      <Badge variant="secondary" className="text-xs">+{match.teachingMatches.length - 3}</Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                              {match.learningMatches.length > 0 && (
                                <div>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
                                    <BookOpen className="h-3 w-3 text-primary" />
                                    Wants to learn:
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {match.learningMatches.slice(0, 3).map(skill => (
                                      <Badge key={skill.name} variant="outline" className="text-xs border-primary/50">
                                        {skill.emoji} {skill.name}
                                      </Badge>
                                    ))}
                                    {match.learningMatches.length > 3 && (
                                      <Badge variant="secondary" className="text-xs">+{match.learningMatches.length - 3}</Badge>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 mt-2">
                            <Button 
                              size="sm" 
                              className="flex-1 gap-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartChat(targetUser.id);
                              }}
                            >
                              <MessageSquare className="h-3 w-3" />
                              Chat
                            </Button>
                            {recentlyActive && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                Active
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
            
            {/* No matches yet - empty state */}
            {matchedUsers.length === 0 && likedUsers.length === 0 && (
              <Card className="p-8 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                {isAuthenticated ? (
                  <>
                    <h4 className="font-semibold mb-1">No skill matches yet</h4>
                    <p className="text-sm text-muted-foreground">
                      Add more skills to find your perfect learning partners!
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setShowManageSkills(true)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Skills
                    </Button>
                  </>
                ) : (
                  <>
                    <h4 className="font-semibold mb-1">Login or Signup and add your skills to find matches</h4>
                    <p className="text-sm text-muted-foreground">
                      Create an account to start finding learning partners!
                    </p>
                    <Button 
                      className="mt-4"
                      onClick={() => navigate('/auth')}
                    >
                      Get Started
                    </Button>
                  </>
                )}
              </Card>
            )}
          </TabsContent>
          
          {/* Everyone Tab - List View */}
          <TabsContent value="everyone" className="space-y-3 mt-4">
            {everyoneUsers.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h4 className="font-semibold mb-1">No users available</h4>
                <p className="text-sm text-muted-foreground">
                  Check back later to find learning partners!
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {everyoneUsers.map((targetUser) => {
                  const match = checkSkillMatch(targetUser);
                  const hasMatch = match !== null;
                  const recentlyActive = isRecentlyActive(targetUser);
                  return (
                    <Card 
                      key={targetUser.id} 
                      className={`p-4 shadow-soft cursor-pointer transition-all hover:shadow-md ${hasMatch ? 'border-primary/50 bg-primary/5' : ''}`}
                      onClick={() => handleUserClick(targetUser)}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-14 w-14 border-2 border-primary/20">
                          <AvatarImage 
                            src={targetUser.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${targetUser.id}`} 
                            alt={targetUser.name || "User"} 
                          />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {(targetUser.name || "U").charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold truncate text-foreground">{targetUser.name || "New User"}</h4>
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="h-4 w-4 fill-current" />
                              <span className="text-sm font-medium">{(targetUser.rating || 0).toFixed(1)}</span>
                            </div>
                          </div>
                          
                          {targetUser.bio && (
                            <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{targetUser.bio}</p>
                          )}
                          
                          {targetUser.teaching_skills.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                                <GraduationCap className="h-3 w-3" /> Can teach:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {targetUser.teaching_skills.slice(0, 3).map(skill => (
                                  <Badge key={skill.id} variant="default" className="text-xs">
                                    {skill.emoji} {skill.name}
                                  </Badge>
                                ))}
                                {targetUser.teaching_skills.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{targetUser.teaching_skills.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {targetUser.learning_skills.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                                <BookOpen className="h-3 w-3" /> Wants to learn:
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {targetUser.learning_skills.slice(0, 3).map(skill => (
                                  <Badge key={skill.id} variant="outline" className="text-xs">
                                    {skill.emoji} {skill.name}
                                  </Badge>
                                ))}
                                {targetUser.learning_skills.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{targetUser.learning_skills.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="secondary" className="text-xs">
                                {targetUser.sessions_completed} sessions
                              </Badge>
                              {recentlyActive && (
                                <Badge className="bg-orange-500 text-white text-xs">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Active
                                </Badge>
                              )}
                              {hasMatch && (
                                <Badge className="bg-green-500 text-white text-xs">
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  Match!
                                </Badge>
                              )}
                            </div>
                            
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartChat(targetUser.id);
                              }}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>


      {/* Manage Skills Dialog */}
      <ManageSkillsDialog 
        open={showManageSkills} 
        onOpenChange={setShowManageSkills} 
      />

      <Navigation />
    </div>
  );
};

export default Match;
